import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { pad } from "@/lib/format";
import {
  getSearchIndex,
  searchEntries,
  type SearchEntry,
} from "@/lib/searchIndex";

interface SearchOverlayProps {
  open: boolean;
  onClose: () => void;
}

export function SearchOverlay({ open, onClose }: SearchOverlayProps) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const [entries, setEntries] = useState<readonly SearchEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load the index lazily on first open.
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getSearchIndex()
      .then((idx) => setEntries(idx))
      .finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQ("");
      setActive(0);
    }
  }, [open]);

  const results = useMemo(() => searchEntries(entries, q), [entries, q]);

  useEffect(() => setActive(0), [q]);

  function onKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") onClose();
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    }
    if (e.key === "Enter" && results[active]) {
      const r = results[active];
      navigate(`/word/${r.day}/${r.index}`);
      onClose();
    }
  }

  if (!open) return null;

  return (
    <div className="search-backdrop" onClick={onClose}>
      <div className="search-panel" onClick={(e) => e.stopPropagation()}>
        <div className="search-head">
          <span className="t-mono search-prompt">⌘K</span>
          <input
            ref={inputRef}
            className="search-input"
            placeholder="Search words, themes…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            spellCheck={false}
            autoComplete="off"
          />
          <span className="t-meta search-meta">
            {loading ? "loading…" : `${results.length} results`}
          </span>
        </div>
        <div className="search-results">
          {!loading && results.length === 0 && (
            <div className="empty" style={{ margin: "var(--s-4)" }}>No matches.</div>
          )}
          {results.map((r, i) => (
            <button
              key={`${r.day}-${r.index}`}
              className={`search-row ${i === active ? "is-active" : ""}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => {
                navigate(`/word/${r.day}/${r.index}`);
                onClose();
              }}
            >
              <span className="t-mono search-row__day">D{pad(r.day)}</span>
              <span className="t-display search-row__word">{r.word}</span>
              <span className="t-mono search-row__ipa">/{r.ipa}/</span>
              <span className="pill pill-pos">{r.pos}</span>
              <span className="t-display italic search-row__meaning">{r.meaning_vi}</span>
              <span className="t-meta search-row__theme">{r.theme_en}</span>
            </button>
          ))}
        </div>
        <div className="search-foot t-mono">
          <span>↑↓ navigate</span>
          <span>↵ open</span>
          <span>esc close</span>
        </div>
      </div>
    </div>
  );
}
